import React, { useEffect, useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

type DateRangePickerProps = {
  from?: string; // YYYY-MM-DD
  to?: string; // YYYY-MM-DD
  initialMonth?: string; // YYYY-MM-DD (anchor month)
  onApply: (from: string, to: string) => void;
  onCancel: () => void;
  className?: string;
};

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  from,
  to,
  initialMonth,
  onApply,
  onCancel,
  className = "",
}) => {
  const today = useMemo(() => dayjs().startOf("day"), []);
  const [pickerMonth, setPickerMonth] = useState<Dayjs>(() =>
    (initialMonth ? dayjs(initialMonth) : from ? dayjs(from) : dayjs()).startOf(
      "month"
    )
  );
  const [selecting, setSelecting] = useState(false);
  const [tempStart, setTempStart] = useState<Dayjs | null>(
    from ? dayjs(from) : null
  );
  const [hoverEnd, setHoverEnd] = useState<Dayjs | null>(to ? dayjs(to) : null);

  // Sync when applied range changes (controlled input)
  useEffect(() => {
    if (from) setTempStart(dayjs(from).startOf("day"));
    if (to) setHoverEnd(dayjs(to).startOf("day"));
    if (from) setPickerMonth(dayjs(from).startOf("month"));
  }, [from, to]);

  // Single-month view
  const month = useMemo(() => pickerMonth, [pickerMonth]);

  const renderMonth = (base: Dayjs, key: number) => {
    const start = base.startOf("month").startOf("week");
    const end = base.endOf("month").endOf("week");
    const cells: Dayjs[] = [];
    let c = start;
    while (c.isBefore(end) || c.isSame(end, "day")) {
      cells.push(c);
      c = c.add(1, "day");
    }
    const hasTemp = !!(tempStart && hoverEnd);
    const selStart = hasTemp ? tempStart : from ? dayjs(from) : null;
    const selEnd = hasTemp ? hoverEnd : to ? dayjs(to) : null;

    return (
      <div key={key} className="text-sm">
        <div className="grid grid-cols-7 text-xs text-gray-400 mb-1">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="text-center py-1">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d) => {
            const inMonth = d.month() === base.month();
            const disabled = d.isAfter(today);
            const min =
              selStart && selEnd
                ? selStart.isBefore(selEnd)
                  ? selStart
                  : selEnd
                : selStart;
            const max =
              selStart && selEnd
                ? selEnd.isAfter(selStart)
                  ? selEnd
                  : selStart
                : selEnd;
            return (
              <div
                key={d.toString()}
                onClick={(evt) => {
                  if (disabled) return;
                  const dd = d.startOf("day");
                  if (!selecting && (!tempStart || (tempStart && hoverEnd))) {
                    setTempStart(dd);
                    setHoverEnd(dd);
                    setSelecting(true);
                  } else if (selecting) {
                    const start =
                      tempStart && tempStart.isBefore(dd) ? tempStart : dd;
                    const end =
                      tempStart && dd.isAfter(tempStart) ? dd : tempStart;
                    setTempStart(start.startOf("day"));
                    if (end) {
                      setHoverEnd(end.startOf("day"));
                    }
                    setSelecting(false);
                  } else if (!selecting && tempStart && !hoverEnd) {
                    const start = tempStart.isBefore(dd) ? tempStart : dd;
                    const end = dd.isAfter(tempStart) ? dd : tempStart;
                    setTempStart(start.startOf("day"));
                    setHoverEnd(end.startOf("day"));
                    setSelecting(false);
                  }
                  evt.preventDefault();
                }}
                onMouseEnter={() => {
                  if (selecting && !disabled) setHoverEnd(d.startOf("day"));
                }}
                className={[
                  "select-none px-0.5 py-1 text-center border transition-colors",
                  inMonth
                    ? "border-white/10"
                    : "border-transparent text-gray-500",
                  disabled
                    ? "opacity-40 cursor-not-allowed"
                    : "cursor-pointer hover:bg-white/10",
                  min &&
                  max &&
                  d.isAfter(min.subtract(1, "day")) &&
                  d.isBefore(max.add(1, "day")) &&
                  !(min && d.isSame(min, "day")) &&
                  !(max && d.isSame(max, "day"))
                    ? "bg-indigo-500/30"
                    : "",
                  (min && d.isSame(min, "day")) || (max && d.isSame(max, "day"))
                    ? "bg-indigo-500/40 text-white font-semibold ring-1 ring-indigo-400 rounded-full"
                    : "",
                ].join(" ")}
                title={d.format("DD MMM YYYY")}
              >
                {d.date()}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={className}>
      <div className="flex items-center justify-center mb-2 gap-3">
        <div className="inline-flex justify-center items-center gap-2">
          <button
            className="btn-ghost px-3 py-1 w-10"
            onClick={() => setPickerMonth((m) => m.subtract(1, "month"))}
            aria-label="Previous month"
            title="Previous month"
          >
            <ChevronLeftIcon />
          </button>
          <div className="text-white/90 text-sm min-w-[160px] text-center select-none">
            {pickerMonth.format("MMMM YYYY")}
          </div>
          <button
            className="btn-ghost px-3 py-1 w-10"
            onClick={() => setPickerMonth((m) => m.add(1, "month"))}
            aria-label="Next month"
            title="Next month"
          >
            <ChevronRightIcon />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4">{renderMonth(month, 0)}</div>
      {/* {summary && <div className="mt-2 text-xs text-white/80">{summary}</div>} */}
      <div className="flex items-center justify-end mt-3 gap-2">
       
        <button className="btn-outline px-3 py-1" onClick={onCancel}>
          Cancel
        </button>
        <button
          className="btn-primary px-3 py-1"
          onClick={() => {
            if (!tempStart || !hoverEnd) return;
            const min = tempStart.isBefore(hoverEnd) ? tempStart : hoverEnd;
            const max = hoverEnd.isAfter(tempStart) ? hoverEnd : tempStart;
            const safeMax = max.isAfter(today) ? today : max;
            onApply(min.format("YYYY-MM-DD"), safeMax.format("YYYY-MM-DD"));
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );
};

export default DateRangePicker;
